import { create } from "zustand";

export type Locale = "es" | "en";

const es = {
  appName: ".MAYUSCULANOTE",
  tagline: "Tableros para pensar en equipo",
  email: "Correo",
  password: "Contraseña",
  confirmPassword: "Confirmar contraseña",
  name: "Nombre",
  signIn: "Entrar",
  signInWithPassword: "Con contraseña",
  signInWithMagic: "Con enlace por correo",
  sendMagic: "Enviarme el enlace",
  magicSent: "Listo. Revisa tu correo y abre el enlace para entrar. Vence en 15 minutos.",
  magicInvalid: "Este enlace ya no es válido. Pide uno nuevo.",
  noAccount: "¿No tienes cuenta?",
  createAccount: "Crear cuenta",
  haveAccount: "¿Ya tienes cuenta?",
  register: "Crear cuenta",
  forgot: "¿Olvidaste tu contraseña?",
  recover: "Recuperar contraseña",
  sendRecovery: "Enviar instrucciones",
  recoverySent: "Si el correo existe, te enviamos instrucciones para cambiar la contraseña.",
  newPassword: "Nueva contraseña",
  changePassword: "Cambiar contraseña",
  passwordChanged: "Tu contraseña cambió. Ya puedes entrar.",
  verifyTitle: "Confirma tu correo",
  verifyBody: "Te enviamos un correo con un enlace para activar tu cuenta.",
  signOut: "Salir",
  home: "Inicio",
  sharedWithMe: "Compartidos conmigo",
  nothingShared: "Aún no te han compartido ningún tablero.",
  noAccess: "Tu cuenta no pertenece a ningún equipo. Pide una invitación al administrador.",
  newBoard: "Nuevo tablero",
  rename: "Renombrar",
  moveToTrash: "Enviar a la papelera",
  confirmTrash: (title: string) => `¿Enviar "${title}" y todo su contenido a la papelera?`,
  trash: "Papelera",
  restore: "Restaurar",
  emptyBoard: "Este tablero está vacío.",
  emptyBoardHow:
    "Doble clic en el lienzo para escribir una nota, arrastra imágenes o archivos desde tu computadora, o elige un tipo de tarjeta en la barra de la izquierda.",
  emptyBoardViewer: "Este tablero aún no tiene contenido.",
  tileHint: "doble clic para abrir",
  boardNotFound: "No encontramos este tablero.",
  usage: {
    title: "Uso",
    hint: "Quién usa .MAYUSCULANOTE, cuánto y qué hace. Últimos 30 días. Un minuto activo es un minuto en el que la persona escribió, hizo clic o se desplazó.",
    off: "El registro de uso está apagado; estos datos no se actualizan.",
    user: "Usuario",
    lastActive: "Última actividad",
    active7: "Activo 7 días",
    active30: "Activo 30 días",
    days30: "Días",
    boards: "Tableros",
    boardsTitle: (n: number) => `${n} aperturas de tablero`,
    cards: "Tarjetas",
    comments: "Comentarios",
    uploads: "Archivos",
    searches: "Búsquedas",
    exports: "Exportaciones",
    dailyTitle: "Minutos activos por día (14 días)",
    recentTitle: "Actividad reciente",
    never: "nunca",
    minutesAgo: (n: number) => `hace ${n} min`,
    hoursAgo: (n: number) => `hace ${n} h`,
    daysAgo: (n: number) => `hace ${n} días`,
    kinds: {
      board_open: "abrió el tablero",
      card_create: "creó una tarjeta",
      comment: "comentó en",
      upload: "subió un archivo a",
      export: "exportó",
      search: "buscó",
      publish: "publicó",
      invite: "invitó a alguien a",
      feedback: "reportó un fallo",
      present: "presentó",
      quick_note: "escribió una nota rápida",
    } as Record<string, string>,
    settingTitle: "Registro de uso",
    settingHint:
      "Durante la fase de pruebas se registra quién usa la herramienta, cuánto tiempo y qué hace (abrir tableros, crear tarjetas, subir archivos…). Apágalo cuando ya no lo necesites; los datos guardados se conservan.",
    settingOn: "Registro activo",
    settingOff: "Registro apagado",
    view: "Ver uso",
  },
  boardPrivate: "Este tablero es privado. Solo lo ve quien lo hizo privado y las personas que invitó.",
  visibility: {
    label: "Visibilidad",
    team: "Equipo",
    private: "Privado",
    teamHint: "Todo el equipo lo ve y lo edita.",
    privateHint:
      "Solo tú lo ves, con todo lo que hay dentro. Puedes invitar a personas concretas desde Compartir.",
    rootPill: "Equipo · lo ven todos",
    rootHint:
      "Inicio es del equipo y no se puede hacer privado. Lo que quieras guardar para ti, ponlo en un tablero privado.",
    teamPill: "Equipo",
    privatePill: "Privado",
    via: (title: string, owner: string | null) =>
      owner ? `Privado: está dentro de «${title}» (de ${owner})` : `Privado: está dentro de «${title}»`,
    lockedTile: "Tablero privado",
    lockedHint: "Privado de otra persona",
  },
  team: "Equipo",
  invite: "Invitar",
  inviteStaff: "Invitar a alguien del equipo",
  inviteGuest: "Compartir con un cliente",
  exportBoard: "Exportar",
  share: "Compartir",
  close: "Cerrar",
  present: "Presentar",
  overview: "Vista general",
  exitPresentation: "Salir",
  shortcutsTitle: "Atajos de teclado",
  shortcutGroups: { canvas: "Lienzo", cards: "Tarjetas", text: "Texto" } as Record<string, string>,
  shortcuts: {
    pan: "Mover el lienzo",
    zoom: "Acercar / alejar",
    zoomFit: "Ver todo",
    resetZoom: "Zoom 100 %",
    newNote: "Nueva nota",
    dblClick: "doble clic",
    search: "Buscar",
    present: "Modo presentación",
    help: "Esta ayuda",
    selectAll: "Seleccionar todo",
    edit: "Editar tarjeta",
    duplicate: "Duplicar",
    connect: "Conectar con línea",
    delete: "Enviar a papelera",
    nudge: "Mover 10 px",
    front: "Traer al frente",
    back: "Enviar atrás",
    undo: "Deshacer / rehacer",
    multi: "Selección múltiple",
    bold: "Negrita",
    italic: "Cursiva",
    link: "Enlace",
    inText: "dentro del texto",
    finish: "Terminar de editar",
  } as Record<string, string>,
  settings: "Ajustes",
  quickNotes: "Notas rápidas",
  guide: "Guía",
  welcomeTitle: (name: string) => (name ? `Hola, ${name}` : "Bienvenido"),
  welcomePoints: [
    "Los tableros se abren con doble clic en su ficha.",
    "Doble clic en el lienzo crea una nota; arrastra imágenes y archivos desde tu computadora.",
    "Clic derecho sobre cualquier cosa muestra lo que puedes hacer con ella.",
  ],
  welcomeGuide: "Ver la guía (5 min)",
  welcomeSkip: "Empezar",
  pushTitle: "Avisos en este dispositivo",
  pushHint:
    "Recibe una notificación cuando te mencionen, comenten en tus tableros o te compartan uno. En iPhone, primero agrega la app a la pantalla de inicio (Compartir → Agregar a inicio).",
  pushUnsupported: "Este navegador no permite notificaciones.",
  pushTurnOn: "Activar avisos",
  pushTurnOff: "Desactivar avisos",
  pushEnabled: "Avisos activados en este dispositivo.",
  pushDisabled: "Avisos desactivados.",
  pushTest: "Enviar aviso de prueba",
  pushTestSent: "Aviso enviado. Debería aparecer en unos segundos.",
  quickNotePlaceholder: "Escribe una idea y pulsa Enter…",
  quickNotesHint: "Tus notas rápidas son personales. Desde un tablero puedes enviarlas al lienzo.",
  quickNotesEmpty: "Aún no tienes notas rápidas.",
  sendToThisBoard: "Enviar a este tablero",
  delete: "Borrar",
  version: "Versión",
  feedback: {
    button: "Reportar fallo",
    hint: "Cuéntanos si algo no funcionó",
    title: "Reportar un fallo",
    intro: "Estamos en fase de pruebas. Si algo no funcionó como esperabas, cuéntanos y lo revisamos.",
    what: "¿Qué pasó?",
    whatPlaceholder: "Por ejemplo: subí una imagen y nunca apareció en el tablero.",
    expected: "¿Qué esperabas que pasara?",
    includeTech:
      "Incluir datos técnicos (página, navegador, versión y los últimos errores del navegador). Ayuda a encontrar la causa.",
    thanks: "Gracias. Recibimos tu reporte y lo revisaremos.",
    failed: "No pudimos enviar el reporte. Intenta de nuevo en un momento.",
    reports: "Reportes",
    reportsHint: "Fallos que los usuarios reportaron desde el botón «Reportar fallo».",
    showResolved: "Mostrar resueltos",
    none: "Sin reportes.",
    openBoard: "Abrir tablero",
    resolve: "Marcar resuelto",
    reopen: "Reabrir",
    details: "Datos técnicos",
    settingTitle: "Reportes de fallos",
    settingHint:
      "Durante la fase de pruebas, todos los usuarios ven el botón «Reportar fallo». Apágalo cuando termine la fase de pruebas.",
    settingOn: "Botón visible para todos",
    settingOff: "Botón oculto",
    viewReports: "Ver reportes",
  },
  profile: "Perfil",
  saved: "Guardado.",
  languageHint: "El idioma de los menús y los correos que te enviamos.",
  passwordHint:
    "Cambia tu contraseña. Si entraste con enlace por correo y aún no tienes una, crea una desde recuperar contraseña.",
  currentPassword: "Contraseña actual",
  passwordUpdated: "Contraseña actualizada. Cerramos tus otras sesiones.",
  passwordTooShort: "La contraseña debe tener al menos 8 caracteres.",
  passwordMismatch: "Las contraseñas no coinciden.",
  noPasswordYet: "¿Aún no tienes contraseña? Crea una",
  workspace: "Espacio de trabajo",
  workspaceName: "Nombre del espacio",
  sessions: "Sesiones",
  sessionsHint: "Cierra la sesión en todos tus otros dispositivos.",
  signOutEverywhere: "Cerrar otras sesiones",
  sessionsRevoked: "Listo. Tus otras sesiones se cerraron.",
  search: "Buscar",
  boardSettings: "Ajustes del tablero",
  boardTitle: "Título",
  boardIcon: "Ícono",
  boardIconLetter: "Usar la inicial",
  boardIconColor: "Color del ícono",
  boardBackground: "Fondo del lienzo",
  customColor: "Color personalizado",
  searchPlaceholder: "Buscar tableros y tarjetas…",
  searchHint: "Escribe al menos 2 letras. Atajo: Ctrl+/",
  searchNoResults: "Sin resultados",
  searchBoards: "Tableros",
  searchCards: "Tarjetas",
  exportFormats: {
    pdf: "PDF (imagen del tablero)",
    "pdf-light": "PDF con fondo claro (para imprimir)",
    png: "PNG (imagen del tablero)",
    "png-light": "PNG con fondo claro",
    docx: "Word (.docx)",
    md: "Markdown (.md)",
    txt: "Texto plano (.txt)",
    zip: "ZIP con archivos",
  } as Record<string, string>,
  exportHint: "PDF y PNG tardan unos segundos en generarse.",
  role: "Rol",
  roles: {
    viewer: "Solo ver",
    commenter: "Comentar",
    editor: "Editar",
    owner: "Equipo",
    admin: "Administrador",
    member: "Miembro",
    none: "Sin acceso",
  } as Record<string, string>,
  inviteSent: "Invitación enviada.",
  acceptInvite: "Aceptar invitación",
  inviteExpired: "Esta invitación ya no es válida.",
  inviteForTeam: (ws: string) => `Te invitaron al equipo de ${ws}`,
  inviteForBoard: (board: string) => `Te invitaron al tablero "${board}"`,
  emailMismatch: "Esta invitación es para otro correo. Entra con la cuenta correcta.",
  loading: "Cargando…",
  error: "Algo salió mal. Intenta de nuevo.",
  save: "Guardar",
  cancel: "Cancelar",
  back: "Volver",
  members: "Personas con acceso",
  pending: "Invitaciones pendientes",
  shareLink: "Enlace compartible",
  copyLink: "Copiar enlace",
  linkCopied: "Enlace copiado.",
  remove: "Quitar",
  language: "Idioma",
  canvasSoon: "El lienzo llega en la siguiente fase. Por ahora puedes organizar tableros.",
  notePlaceholder: "Escribe algo…",
  linkShort: "Enlace",
  tableTitle: "Título de la tabla",
  row: "fila",
  column: "columna",
  formulaHint: "Fórmulas: =SUM(A1:A3)",
  headingPlaceholder: "Encabezado",
  tools: { select: "Seleccionar (V)", hand: "Mover lienzo (espacio)" } as Record<string, string>,
  cardTypes: {
    note: "Nota",
    heading: "Encabezado",
    image: "Imagen",
    link: "Enlace",
    board: "Tablero",
    column: "Columna",
    todo: "Lista de tareas",
    comment: "Comentario",
    line: "Línea",
    swatch: "Color",
    sketch: "Boceto",
    table: "Tabla",
    document: "Documento",
    video: "Video",
    audio: "Audio",
    map: "Mapa",
    file: "Archivo",
    alias: "Acceso directo",
    annotation: "Dibujo en el lienzo",
    clone: "Nota sincronizada",
  } as Record<string, string>,
  aliasSearch: "Buscar tablero…",
  searchLabels: "Buscar o crear etiqueta…",
  createLabel: (n: string) => `Crear etiqueta "${n}"`,
  deletedByMe: "Borrado por mí",
  deletedByOthers: "Borrado por otros",
  deleteForever: "Eliminar definitivamente",
  chooseTemplate: "Elige una plantilla",
  searchTemplates: "Buscar plantillas…",
  all: "Todas",
  keepExampleContent: "Mantener contenido de ejemplo",
  emptyBoardTpl: "Tablero vacío",
  emptyBoardTplHint: "Empieza desde cero.",
  useTemplate: "Usar plantilla",
  templates: "Plantillas",
  convertToTemplate: "Convertir en plantilla",
  templateSaved: "Plantilla guardada. La encontrarás en la galería.",
  confirmDeleteTemplate: (n: string) => `¿Eliminar la plantilla "${n}"?`,
  history: "Historial",
  versionLabel: "Nombre de la versión",
  versionLabelPlaceholder: "p. ej. Antes de presentar al cliente",
  saveVersion: "Guardar versión",
  noVersions: "Aún no hay versiones. Se guardan automáticamente cada 10 minutos de cambios.",
  versionKinds: {
    auto: "Automática",
    manual: "Manual",
    restore: "Restaurada",
    "pre-restore": "Copia previa a restaurar",
  } as Record<string, string>,
  cards: "tarjetas",
  confirmRestore: (when: string) =>
    `¿Restaurar el tablero a la versión del ${when}? Se guardará una copia del estado actual.`,
  activity: "Actividad",
  you: "tú",
  tabs: { notifications: "Avisos", activity: "Actividad", online: "En línea" } as Record<string, string>,
  notif: {
    mention: (a: string, b: string) => `${a} te mencionó en "${b}"`,
    comment: (a: string, b: string) => `${a} comentó en "${b}"`,
    shared: (a: string, b: string) => `${a} compartió contigo "${b}"`,
  },
  act: {
    edits: (a: number, r: number) =>
      `${a ? `añadió ${a} tarjeta${a === 1 ? "" : "s"}` : ""}${a && r ? " y " : ""}${r ? `eliminó ${r}` : ""}`,
    comment: "comentó",
    joined: "se unió al tablero",
  },
  mentionHint: "Escribe @ para mencionar",
  sharedBoard: "Tablero compartido",
  shareNotFound: "Este enlace ya no está disponible.",
  protectedBoard: "Este tablero está protegido con contraseña.",
  wrongPassword: "Contraseña incorrecta.",
  open: "Abrir",
  publicLink: "Enlace público (solo lectura)",
  enableLink: "Activar enlace",
  disableLink: "Desactivar",
  resetLink: "Generar nuevo enlace",
  requirePassword: "Pedir contraseña",
  allowComments: "Permitir comentarios (con cuenta)",
  allowDownload: "Mostrar descargas",
  showAuthors: "Mostrar autores",
  welcomeMessage: "Mensaje de bienvenida",
  views: "visitas",
  madeWith: "Hecho con",
  unsorted: "Sin clasificar",
  unsortedHint: "Tarjetas que llegaron a este tablero y aún no están en el lienzo.",
  place: "Colocar",
  menu: {
    toUnsorted: "Mover a Sin clasificar",
    labels: "Etiquetas…",
    reaction: "Reacción…",
    duplicate: "Duplicar",
    bringToFront: "Traer al frente",
    sendToBack: "Enviar atrás",
    lock: "Bloquear / desbloquear",
    newNote: "Nueva nota",
    newHeading: "Nuevo encabezado",
    newBoard: "Nuevo tablero",
    paste: "Pegar",
    zoomFit: "Ver todo (Z)",
    boardSettings: "Ajustes del tablero…",
    openBoard: "Abrir tablero",
    duplicateBoard: "Duplicar tablero (con sub-tableros)",
    align: "Alinear",
    alignLeft: "Alinear a la izquierda",
    alignCenterX: "Centrar horizontalmente",
    alignRight: "Alinear a la derecha",
    alignTop: "Alinear arriba",
    alignCenterY: "Centrar verticalmente",
    alignBottom: "Alinear abajo",
    distributeX: "Distribuir horizontalmente",
    distributeY: "Distribuir verticalmente",
    boardShortcut: "Acceso directo a un tablero…",
    draw: "Dibujar en el lienzo",
    makeSynced: "Crear nota sincronizada",
    copySynced: "Copiar como nota sincronizada (para otro tablero)",
    pasteSynced: "Pegar nota sincronizada",
    preventOverlap: "Evitar superposición",
  } as Record<string, string>,
  status: {
    connecting: "Conectando…",
    connected: "Guardado",
    disconnected: "Sin conexión, reintentando…",
    readOnly: "Solo lectura",
  } as Record<string, string>,
  uploadImage: "Subir imagen",
  uploadFile: "Subir archivo",
  todoTitle: "Título de la lista",
  todoItem: "Nueva tarea",
  columnTitle: "Nueva columna",
  linkPlaceholder: "Pega un enlace",
  commentStarted: (name: string) => `${name} inició este hilo`,
  writeComment: "Escribe un comentario…",
  reply: "Responder…",
  send: "Enviar",
  sketchHint: "Doble clic para dibujar",
  done: "Listo",
  openDocument: "Doble clic para abrir",
  connectHint: "Selecciona dos o más tarjetas y pulsa Ctrl+K para conectarlas",
  syncing: "Sincronizando…",
  errors: {
    push_denied: "El navegador bloqueó las notificaciones. Permítelas en los ajustes del sitio.",
    push_unavailable: "Las notificaciones no están disponibles en este momento.",
    INVALID_EMAIL_OR_PASSWORD: "Correo o contraseña incorrectos.",
    EMAIL_NOT_VERIFIED: "Confirma tu correo antes de entrar. Te reenviamos el enlace.",
    USER_ALREADY_EXISTS: "Ya existe una cuenta con ese correo.",
    PASSWORD_TOO_SHORT: "La contraseña debe tener al menos 8 caracteres.",
    PASSWORD_MISMATCH: "Las contraseñas no coinciden.",
    INVALID_TOKEN: "El enlace ya no es válido.",
    TOO_MANY_REQUESTS: "Demasiados intentos. Espera un momento.",
  } as Record<string, string>,
};

export type Dict = typeof es;

const en: Dict = {
  appName: ".MAYUSCULANOTE",
  tagline: "Boards for thinking together",
  email: "Email",
  password: "Password",
  confirmPassword: "Confirm password",
  name: "Name",
  signIn: "Sign in",
  signInWithPassword: "With password",
  signInWithMagic: "With a link by email",
  sendMagic: "Send me the link",
  magicSent: "Done. Check your email and open the link to sign in. It expires in 15 minutes.",
  magicInvalid: "This link is no longer valid. Request a new one.",
  noAccount: "No account yet?",
  createAccount: "Create account",
  haveAccount: "Already have an account?",
  register: "Create account",
  forgot: "Forgot your password?",
  recover: "Recover password",
  sendRecovery: "Send instructions",
  recoverySent: "If that email exists, we sent instructions to change the password.",
  newPassword: "New password",
  changePassword: "Change password",
  passwordChanged: "Your password was changed. You can sign in now.",
  verifyTitle: "Confirm your email",
  verifyBody: "We sent you an email with a link to activate your account.",
  signOut: "Sign out",
  home: "Home",
  sharedWithMe: "Shared with me",
  nothingShared: "No boards have been shared with you yet.",
  noAccess: "Your account doesn't belong to a team. Ask an administrator for an invitation.",
  newBoard: "New board",
  rename: "Rename",
  moveToTrash: "Move to trash",
  confirmTrash: (title: string) => `Move "${title}" and everything inside it to the trash?`,
  trash: "Trash",
  restore: "Restore",
  emptyBoard: "This board is empty.",
  emptyBoardHow:
    "Double-click the canvas to write a note, drag images or files from your computer, or pick a card type from the toolbar on the left.",
  emptyBoardViewer: "This board has no content yet.",
  tileHint: "double-click to open",
  boardNotFound: "We couldn't find this board.",
  usage: {
    title: "Usage",
    hint: "Who uses .MAYUSCULANOTE, how much and what they do. Last 30 days. An active minute is one in which the person typed, clicked or scrolled.",
    off: "Usage tracking is off; these figures are not being updated.",
    user: "User",
    lastActive: "Last active",
    active7: "Active 7 days",
    active30: "Active 30 days",
    days30: "Days",
    boards: "Boards",
    boardsTitle: (n: number) => `${n} board opens`,
    cards: "Cards",
    comments: "Comments",
    uploads: "Files",
    searches: "Searches",
    exports: "Exports",
    dailyTitle: "Active minutes per day (14 days)",
    recentTitle: "Recent activity",
    never: "never",
    minutesAgo: (n: number) => `${n} min ago`,
    hoursAgo: (n: number) => `${n} h ago`,
    daysAgo: (n: number) => `${n} days ago`,
    kinds: {
      board_open: "opened board",
      card_create: "created a card",
      comment: "commented on",
      upload: "uploaded a file to",
      export: "exported",
      search: "searched",
      publish: "published",
      invite: "invited someone to",
      feedback: "reported a problem",
      present: "presented",
      quick_note: "wrote a quick note",
    },
    settingTitle: "Usage tracking",
    settingHint:
      "During the testing phase we record who uses the tool, for how long and what they do (opening boards, creating cards, uploading files…). Switch it off when you no longer need it; stored data is kept.",
    settingOn: "Tracking on",
    settingOff: "Tracking off",
    view: "View usage",
  },
  boardPrivate:
    "This board is private. Only the person who made it private and the people they invited can see it.",
  visibility: {
    label: "Visibility",
    team: "Team",
    private: "Private",
    teamHint: "The whole team sees and edits it.",
    privateHint: "Only you see it, with everything inside. You can invite specific people from Share.",
    rootPill: "Team · everyone sees it",
    rootHint: "Inicio belongs to the team and cannot be made private. Keep personal work in a private board.",
    teamPill: "Team",
    privatePill: "Private",
    via: (title: string, owner: string | null) =>
      owner ? `Private: it is inside “${title}” (${owner}'s)` : `Private: it is inside “${title}”`,
    lockedTile: "Private board",
    lockedHint: "Someone else's private board",
  },
  team: "Team",
  invite: "Invite",
  inviteStaff: "Invite a team member",
  inviteGuest: "Share with a client",
  exportBoard: "Export",
  share: "Share",
  close: "Close",
  present: "Present",
  overview: "Overview",
  exitPresentation: "Exit",
  shortcutsTitle: "Keyboard shortcuts",
  shortcutGroups: { canvas: "Canvas", cards: "Cards", text: "Text" },
  shortcuts: {
    pan: "Pan the canvas",
    zoom: "Zoom in / out",
    zoomFit: "Fit all",
    resetZoom: "Zoom 100%",
    newNote: "New note",
    dblClick: "double-click",
    search: "Search",
    present: "Presentation mode",
    help: "This help",
    selectAll: "Select all",
    edit: "Edit card",
    duplicate: "Duplicate",
    connect: "Connect with a line",
    delete: "Move to trash",
    nudge: "Nudge 10 px",
    front: "Bring to front",
    back: "Send to back",
    undo: "Undo / redo",
    multi: "Multi-select",
    bold: "Bold",
    italic: "Italic",
    link: "Link",
    inText: "inside text",
    finish: "Finish editing",
  },
  settings: "Settings",
  quickNotes: "Quick notes",
  guide: "Guide",
  welcomeTitle: (name: string) => (name ? `Hi, ${name}` : "Welcome"),
  welcomePoints: [
    "Boards open with a double-click on their tile.",
    "Double-click the canvas to create a note; drag images and files from your computer.",
    "Right-click anything to see what you can do with it.",
  ],
  welcomeGuide: "Read the guide (5 min)",
  welcomeSkip: "Get started",
  pushTitle: "Notifications on this device",
  pushHint:
    "Get a notification when you are mentioned, when someone comments on your boards, or when a board is shared with you. On iPhone, first add the app to the home screen (Share → Add to Home Screen).",
  pushUnsupported: "This browser does not support notifications.",
  pushTurnOn: "Turn on notifications",
  pushTurnOff: "Turn off notifications",
  pushEnabled: "Notifications enabled on this device.",
  pushDisabled: "Notifications disabled.",
  pushTest: "Send a test notification",
  pushTestSent: "Sent. It should appear in a few seconds.",
  quickNotePlaceholder: "Type an idea and press Enter…",
  quickNotesHint: "Quick notes are personal. From a board you can send them onto the canvas.",
  quickNotesEmpty: "No quick notes yet.",
  sendToThisBoard: "Send to this board",
  delete: "Delete",
  version: "Version",
  feedback: {
    button: "Report a problem",
    hint: "Tell us if something didn't work",
    title: "Report a problem",
    intro:
      "We are in a testing phase. If something didn't work as you expected, tell us and we'll look into it.",
    what: "What happened?",
    whatPlaceholder: "For example: I uploaded an image and it never showed up on the board.",
    expected: "What did you expect to happen?",
    includeTech:
      "Include technical details (page, browser, version and the latest browser errors). It helps find the cause.",
    thanks: "Thank you. We received your report and will look into it.",
    failed: "We couldn't send the report. Please try again in a moment.",
    reports: "Reports",
    reportsHint: "Problems users reported with the “Report a problem” button.",
    showResolved: "Show resolved",
    none: "No reports.",
    openBoard: "Open board",
    resolve: "Mark resolved",
    reopen: "Reopen",
    details: "Technical details",
    settingTitle: "Problem reports",
    settingHint:
      "During the testing phase every user sees the “Report a problem” button. Switch it off once testing is over.",
    settingOn: "Button visible to everyone",
    settingOff: "Button hidden",
    viewReports: "View reports",
  },
  profile: "Profile",
  saved: "Saved.",
  languageHint: "The language of menus and of the emails we send you.",
  passwordHint:
    "Change your password. If you signed in with an email link and have none yet, create one from password recovery.",
  currentPassword: "Current password",
  passwordUpdated: "Password updated. Your other sessions were signed out.",
  passwordTooShort: "The password must be at least 8 characters.",
  passwordMismatch: "The passwords do not match.",
  noPasswordYet: "No password yet? Create one",
  workspace: "Workspace",
  workspaceName: "Workspace name",
  sessions: "Sessions",
  sessionsHint: "Sign out on all your other devices.",
  signOutEverywhere: "Sign out other sessions",
  sessionsRevoked: "Done. Your other sessions were signed out.",
  search: "Search",
  boardSettings: "Board settings",
  boardTitle: "Title",
  boardIcon: "Icon",
  boardIconLetter: "Use the initial",
  boardIconColor: "Icon colour",
  boardBackground: "Canvas background",
  customColor: "Custom colour",
  searchPlaceholder: "Search boards and cards…",
  searchHint: "Type at least 2 letters. Shortcut: Ctrl+/",
  searchNoResults: "No results",
  searchBoards: "Boards",
  searchCards: "Cards",
  exportFormats: {
    pdf: "PDF (board image)",
    "pdf-light": "PDF on a light background (for print)",
    png: "PNG (board image)",
    "png-light": "PNG on a light background",
    docx: "Word (.docx)",
    md: "Markdown (.md)",
    txt: "Plain text (.txt)",
    zip: "ZIP with files",
  },
  exportHint: "PDF and PNG take a few seconds to generate.",
  role: "Role",
  roles: {
    viewer: "View only",
    commenter: "Comment",
    editor: "Edit",
    owner: "Team",
    admin: "Administrator",
    member: "Member",
    none: "No access",
  },
  inviteSent: "Invitation sent.",
  acceptInvite: "Accept invitation",
  inviteExpired: "This invitation is no longer valid.",
  inviteForTeam: (ws: string) => `You've been invited to the ${ws} team`,
  inviteForBoard: (board: string) => `You've been invited to the board "${board}"`,
  emailMismatch: "This invitation is for a different email. Sign in with the right account.",
  loading: "Loading…",
  error: "Something went wrong. Please try again.",
  save: "Save",
  cancel: "Cancel",
  back: "Back",
  members: "People with access",
  pending: "Pending invitations",
  shareLink: "Shareable link",
  copyLink: "Copy link",
  linkCopied: "Link copied.",
  remove: "Remove",
  language: "Language",
  canvasSoon: "The canvas arrives in the next phase. For now you can organize boards.",
  notePlaceholder: "Type something…",
  linkShort: "Link",
  tableTitle: "Table title",
  row: "row",
  column: "column",
  formulaHint: "Formulas: =SUM(A1:A3)",
  headingPlaceholder: "Heading",
  tools: { select: "Select (V)", hand: "Pan canvas (space)" },
  cardTypes: {
    note: "Note",
    heading: "Heading",
    image: "Image",
    link: "Link",
    board: "Board",
    column: "Column",
    todo: "To-do list",
    comment: "Comment",
    line: "Line",
    swatch: "Color",
    sketch: "Sketch",
    table: "Table",
    document: "Document",
    video: "Video",
    audio: "Audio",
    map: "Map",
    file: "File",
    alias: "Board shortcut",
    annotation: "Canvas drawing",
    clone: "Synced note",
  },
  aliasSearch: "Search boards…",
  searchLabels: "Search or create a label…",
  createLabel: (n: string) => `Create label "${n}"`,
  deletedByMe: "Deleted by me",
  deletedByOthers: "Deleted by others",
  deleteForever: "Delete forever",
  chooseTemplate: "Choose a template",
  searchTemplates: "Search templates…",
  all: "All",
  keepExampleContent: "Keep example content",
  emptyBoardTpl: "Empty board",
  emptyBoardTplHint: "Start from scratch.",
  useTemplate: "Use this template",
  templates: "Templates",
  convertToTemplate: "Convert to template",
  templateSaved: "Template saved. You'll find it in the gallery.",
  confirmDeleteTemplate: (n: string) => `Delete the template "${n}"?`,
  history: "History",
  versionLabel: "Version name",
  versionLabelPlaceholder: "e.g. Before client review",
  saveVersion: "Save version",
  noVersions: "No versions yet. They are saved automatically every 10 minutes of changes.",
  versionKinds: {
    auto: "Automatic",
    manual: "Manual",
    restore: "Restored",
    "pre-restore": "Copy before restore",
  },
  cards: "cards",
  confirmRestore: (when: string) =>
    `Restore the board to the version from ${when}? A copy of the current state will be kept.`,
  activity: "Activity",
  you: "you",
  tabs: { notifications: "Notifications", activity: "Activity", online: "Online now" },
  notif: {
    mention: (a: string, b: string) => `${a} mentioned you in "${b}"`,
    comment: (a: string, b: string) => `${a} commented in "${b}"`,
    shared: (a: string, b: string) => `${a} shared "${b}" with you`,
  },
  act: {
    edits: (a: number, r: number) =>
      `${a ? `added ${a} card${a === 1 ? "" : "s"}` : ""}${a && r ? " and " : ""}${r ? `removed ${r}` : ""}`,
    comment: "commented",
    joined: "joined the board",
  },
  mentionHint: "Type @ to mention someone",
  sharedBoard: "Shared board",
  shareNotFound: "This link is no longer available.",
  protectedBoard: "This board is password protected.",
  wrongPassword: "Wrong password.",
  open: "Open",
  publicLink: "Public link (read-only)",
  enableLink: "Enable link",
  disableLink: "Disable",
  resetLink: "Generate new link",
  requirePassword: "Require a password",
  allowComments: "Allow comments (with account)",
  allowDownload: "Show downloads",
  showAuthors: "Show authors",
  welcomeMessage: "Welcome message",
  views: "views",
  madeWith: "Made with",
  unsorted: "Unsorted",
  unsortedHint: "Cards that arrived on this board and aren't on the canvas yet.",
  place: "Place",
  menu: {
    toUnsorted: "Move to Unsorted",
    labels: "Labels…",
    reaction: "Reaction…",
    duplicate: "Duplicate",
    bringToFront: "Bring to front",
    sendToBack: "Send to back",
    lock: "Lock / unlock",
    newNote: "New note",
    newHeading: "New heading",
    newBoard: "New board",
    paste: "Paste",
    zoomFit: "Zoom to fit (Z)",
    boardSettings: "Board settings…",
    openBoard: "Open board",
    duplicateBoard: "Duplicate board (with sub-boards)",
    align: "Align",
    alignLeft: "Align left",
    alignCenterX: "Center horizontally",
    alignRight: "Align right",
    alignTop: "Align top",
    alignCenterY: "Center vertically",
    alignBottom: "Align bottom",
    distributeX: "Distribute horizontally",
    distributeY: "Distribute vertically",
    boardShortcut: "Shortcut to a board…",
    draw: "Draw on the canvas",
    makeSynced: "Create synced note",
    copySynced: "Copy as synced note (for another board)",
    pasteSynced: "Paste synced note",
    preventOverlap: "Prevent overlap",
  },
  status: {
    connecting: "Connecting…",
    connected: "Saved",
    disconnected: "Offline, retrying…",
    readOnly: "Read only",
  },
  uploadImage: "Upload image",
  uploadFile: "Upload file",
  todoTitle: "List title",
  todoItem: "New task",
  columnTitle: "New column",
  linkPlaceholder: "Paste a link",
  commentStarted: (name: string) => `${name} started this thread`,
  writeComment: "Write a comment…",
  reply: "Reply…",
  send: "Send",
  sketchHint: "Double-click to draw",
  done: "Done",
  openDocument: "Double-click to open",
  connectHint: "Select two or more cards and press Ctrl+K to connect them",
  syncing: "Syncing…",
  errors: {
    push_denied: "The browser blocked notifications. Allow them in the site settings.",
    push_unavailable: "Notifications are not available right now.",
    INVALID_EMAIL_OR_PASSWORD: "Wrong email or password.",
    EMAIL_NOT_VERIFIED: "Confirm your email before signing in. We resent the link.",
    USER_ALREADY_EXISTS: "An account with that email already exists.",
    PASSWORD_TOO_SHORT: "The password must be at least 8 characters.",
    PASSWORD_MISMATCH: "The passwords don't match.",
    INVALID_TOKEN: "The link is no longer valid.",
    TOO_MANY_REQUESTS: "Too many attempts. Wait a moment.",
  },
};

export const dictionaries: Record<Locale, Dict> = { es, en };
export const LOCALES: { value: Locale; label: string }[] = [
  { value: "es", label: "Español" },
  { value: "en", label: "English" },
];

function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem("mn_locale");
    if (saved === "es" || saved === "en") return saved;
  } catch {}
  return navigator.language?.toLowerCase().startsWith("en") ? "en" : "es";
}

function persist(locale: Locale) {
  try {
    localStorage.setItem("mn_locale", locale);
  } catch {}
  // The server reads this cookie to pick the language of emails it sends.
  document.cookie = `mn_locale=${locale}; path=/; max-age=31536000; samesite=lax`;
  document.documentElement.lang = locale;
}

interface LocaleState {
  locale: Locale;
  t: Dict;
  setLocale: (l: Locale) => void;
}
const initial = detectLocale();
persist(initial);
export const useLocale = create<LocaleState>((set) => ({
  locale: initial,
  t: dictionaries[initial],
  setLocale: (locale) => {
    persist(locale);
    set({ locale, t: dictionaries[locale] });
  },
}));

/** Current dictionary; re-renders the component when the language changes. */
export const useT = () => useLocale((s) => s.t);
export const errText = (t: Dict, code?: string | null) => (code && t.errors[code]) || t.error;
